/**
 * CreatableListbox
 *
 * Purpose: Custom MUI Autocomplete listbox that makes its first option sticky
 *   at the top of the scroll area. Used by ListFieldRenderer when a field
 *   supports inline MDM value creation (ORCH-128).
 *
 * Architecture: React.forwardRef wrapper around <ul>. MUI passes the ref for
 *   scroll-into-view on keyboard nav and spreads className/style/onScroll/aria
 *   props — all must be forwarded or keyboard/a11y behavior breaks on long lists.
 *
 * Wiring: supply it as `slotProps={{ listbox: { component: CreatableListbox } }}`,
 *   never as `ListboxComponent`. MUI renders the styled listbox `as` this
 *   component, so `.MuiAutocomplete-listbox` (max-height, overflow:auto, option
 *   styles) still applies. `ListboxComponent` is deprecated and MUI 7.3.11
 *   stopped applying it altogether — the prop is read and dropped — which is how
 *   QA, built against a newer MUI than the local lockfile, lost this component
 *   entirely and the gap above "Add New" came back (ORCH-128, 2026-10-07).
 *   `data-testid="creatable-listbox"` lets tests prove the real Autocomplete
 *   rendered *this* <ul> rather than MUI's default.
 *
 * The sticky sentinel is the first <li> rendered by ListFieldRenderer's
 *   renderOption (via filterOptions prepending the ADD_NEW sentinel at index 0).
 *   We rely on the sentinel's own sx styling for position:sticky, zIndex, and
 *   background — MUI's .MuiAutocomplete-listbox class already supplies the
 *   max-height + overflow:auto that makes the <ul> a scroll container.
 *
 * Why paddingTop is zeroed: .MuiAutocomplete-listbox ships `padding: 8px 0`.
 *   Browsers inset the sticky view rectangle by the scroll container's padding,
 *   so `top: 0` pins the sentinel 8px below the scrollport edge — and scrolled
 *   options render into that padding strip, showing through above the sentinel
 *   (ORCH-128 QA: "you see the text behind and it looks funny"). Removing the
 *   top padding makes the sentinel flush with the input above it. Inline style,
 *   not sx, so it beats MUI's own class without !important; the caller's style
 *   (max-height/overflow) is spread first and preserved.
 */

import React from 'react';

export const CreatableListbox = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>(function CreatableListbox({ children, ...props }, ref) {
  return (
    <ul
      ref={ref}
      {...props}
      data-testid="creatable-listbox"
      style={{ ...props.style, paddingTop: 0 }}
    >
      {children}
    </ul>
  );
});
